/**
 * The rounds in flight: one record per round that has left a gun and not yet
 * flown out of range.
 *
 * A record is **a round in flight**, not a streak: it carries the muzzle it
 * left, the unit direction it is flying along, and the tick it was fired, and
 * where it *is* falls out of those three plus the muzzle velocity
 * (`roundPositionAt`). These records are the simulation of the round, not a
 * decoration of it — `collide.ts` sweeps them against the targets on every
 * tick, so what the renderer draws and what scored a hit are the same objects.
 *
 * **Every** round fired gets a record and every record can hit. Only one in
 * `gunTracerInterval` of them is a **tracer**, and a tracer is the only round
 * anybody can see: that is the belt loading, recovered as 1-in-3, and it is a
 * statement about visibility, never about ammunition or lethality. The two
 * were briefly confused when the port drew all of them, on the reasoning that
 * a sparse stream reads badly; the fix for that is the belt the original
 * actually loaded, not a belt of pure tracer.
 *
 * The direction is the gun line (`gunnery.ts`), never a lead solution: rounds
 * fly straight out of the guns whether or not anything is being aimed at, and
 * the ones that miss visibly streak past. Pointing them at the gunnery solver's
 * intercept point makes them bend toward whatever it has picked — including an
 * enemy behind the aeroplane, since `pickTarget` has no boresight limit — and
 * vanish entirely with nothing in view (user report, 2026-08-19: "fire anim is
 * there but no bullets from the gun").
 *
 * They live in the encounter state (and therefore in its hash) rather than in
 * a renderer-side buffer, so a replay draws exactly the rounds the original
 * run drew. The list is capped: a jammed-open trigger cannot grow the state.
 */

import { addVec3, scaleVec3, type Vec3 } from "../flight/math3d.ts";
import type { Tunables } from "../flight/tunables.ts";

export interface RoundRecord {
  readonly shooterId: string;
  /**
   * This round's place in the shooter's running total. It is what decides
   * which barrel fired it (`gunnery.ts`'s `barrelIndexFor`), and the renderer
   * uses the same ordinal to alternate rounds between the *drawn* gun breeches
   * — which come from the cockpit art and need not be the same count as the
   * `.DAT` gun-position field.
   */
  readonly roundIndex: number;
  /** World-space muzzle position at the instant of firing. */
  readonly from: Vec3;
  /** Unit direction of flight — the gun line it left along, fixed for its whole life. */
  readonly direction: Vec3;
  readonly spawnTick: number;
  /**
   * The firing gun's effectiveness (`.DAT` offset 206), carried on the round so
   * a hit can be sized without looking its shooter back up — which matters,
   * because the shooter can be dead by the time its last burst arrives.
   */
  readonly damageRaw: number;
  /** True for the one round in `gunTracerInterval` that is visible. */
  readonly tracer: boolean;
}

/**
 * Hard ceiling on live rounds, so the hashable state stays bounded. One
 * shooter holding the trigger down carries `gunRoundsPerSecond × lifetime` ≈ 48
 * of these, so this is ten aeroplanes firing continuously — well past what a
 * real fight sustains, since the AI shoots in bursts. Past it `pruneRounds`
 * keeps the newest, which is the right thing to lose last: the oldest rounds
 * are the most distant, the least visible, and the least likely to still be
 * near anything.
 *
 * Raised from 256 when hit resolution moved onto these records: an overflowing
 * list used to cost a few drawn dots and now costs *hits*, so the cap has to
 * sit above what the largest supported flight can put in the air rather than
 * merely above what looks right.
 */
export const MAX_LIVE_ROUNDS = 512;

/**
 * How long a round stays in the state: exactly as long as it takes to reach
 * `gunMaxRangeM`, past which `ballistics.ts` treats it as spent anyway. Derived
 * rather than tuned — a separate lifetime constant could only ever disagree
 * with the range the same rounds stop being able to hit at.
 */
export function roundLifetimeTicks(tunables: Tunables, dt: number): number {
  const seconds = tunables.gunMaxRangeM / Math.max(tunables.gunMuzzleSpeedMps, 1);
  return Math.max(1, Math.round(seconds / Math.max(dt, 1e-6)));
}

/** Metres the round has flown by `tick`. Clamped at 0 so a record read on its own spawn tick sits at the muzzle. */
export function roundTravelM(record: RoundRecord, tick: number, dt: number, tunables: Tunables): number {
  return Math.max(0, tick - record.spawnTick) * dt * tunables.gunMuzzleSpeedMps;
}

/** Where the round is at `tick` — muzzle plus travel along its fixed direction. */
export function roundPositionAt(record: RoundRecord, tick: number, dt: number, tunables: Tunables): Vec3 {
  return addVec3(record.from, scaleVec3(record.direction, roundTravelM(record, tick, dt, tunables)));
}

/** Drops rounds past their range and trims the newest ones to the cap. */
export function pruneRounds(
  rounds: readonly RoundRecord[],
  tick: number,
  lifetimeTicks: number,
): readonly RoundRecord[] {
  const live = rounds.filter((round) => tick - round.spawnTick < lifetimeTicks);
  return live.length <= MAX_LIVE_ROUNDS ? live : live.slice(live.length - MAX_LIVE_ROUNDS);
}
